import { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  FiUsers, 
  FiTrendingUp, 
  FiDollarSign, 
  FiPackage,
  FiClock,
  FiShield,
  FiTruck,
  FiHeadphones,
  FiCheckCircle,
  FiBarChart,
  FiFileText,
  FiAward
} from 'react-icons/fi';
import Button from '@components/common/Button';
import Modal from '@components/common/Modal';
import LoginModal from '@components/common/LoginModal';
import QuoteModal from '@components/common/QuoteModal';

const B2BPortalPage = () => {
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isApplyModalOpen, setIsApplyModalOpen] = useState(false);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [activeFaq, setActiveFaq] = useState(null);

  const benefits = [
    {
      icon: FiDollarSign,
      title: 'Trade Pricing',
      description: 'Up to 40% discount on bulk orders and regular purchases',
      detail: 'Volume-based pricing tiers'
    },
    {
      icon: FiPackage,
      title: 'Bulk Ordering',
      description: 'CSV upload for quick bulk order placement',
      detail: 'Process 1000s of items instantly'
    },
    {
      icon: FiTrendingUp,
      title: 'Recurring Orders',
      description: 'Automatic recurring orders for regular supplies',
      detail: 'Set and forget ordering'
    },
    {
      icon: FiUsers,
      title: 'Dedicated Support',
      description: 'Personal account managers available 24/7',
      detail: 'Direct phone & email support'
    },
    {
      icon: FiTruck,
      title: 'Priority Delivery',
      description: 'Next-day delivery on stock items',
      detail: 'Free delivery over £500'
    },
    {
      icon: FiFileText,
      title: 'Net 30 Terms',
      description: 'Flexible payment terms available',
      detail: 'Credit facilities for approved accounts'
    },
    {
      icon: FiBarChart,
      title: 'Analytics Dashboard',
      description: 'Track spending and manage budgets',
      detail: 'Real-time reporting'
    },
    {
      icon: FiShield,
      title: 'Extended Warranty',
      description: 'Additional warranty coverage included',
      detail: 'Up to 5 years protection'
    },
  ];

  const pricingTiers = [
    {
      name: 'Starter',
      spend: '£0 - £5,000/year',
      discount: '10%',
      features: [
        'Trade pricing on all products',
        'Dedicated account manager',
        'Online ordering portal',
        'Standard delivery',
        'Quarterly business reviews',
        'Technical support'
      ],
      color: 'blue'
    },
    {
      name: 'Professional',
      spend: '£5,000 - £25,000/year',
      discount: '20%',
      features: [
        'Everything in Starter',
        'Priority delivery',
        'Net 30 payment terms',
        'Free installation on orders £1000+',
        'Monthly business reviews',
        'Training sessions',
        'Extended warranty'
      ],
      color: 'accent',
      popular: true
    },
    {
      name: 'Enterprise',
      spend: '£25,000+/year',
      discount: '30-40%',
      features: [
        'Everything in Professional',
        'Custom pricing agreements',
        'Same-day delivery available',
        'Dedicated technical team',
        '24/7 priority support',
        'Custom integration support',
        'Free annual maintenance',
        'Volume rebates'
      ],
      color: 'primary'
    },
  ];

  const industries = [
    {
      title: 'Property Management',
      icon: '🏢',
      description: 'Comprehensive security solutions for residential and commercial properties'
    },
    {
      title: 'Retail & Hospitality',
      icon: '🏪',
      description: 'Advanced CCTV and access control systems for retail environments'
    },
    {
      title: 'Construction',
      icon: '🏗️',
      description: 'Temporary and permanent security solutions for building sites'
    },
    {
      title: 'Education',
      icon: '🎓',
      description: 'Safe and compliant security systems for schools and universities'
    },
    {
      title: 'Healthcare',
      icon: '🏥',
      description: 'GDPR-compliant security solutions for medical facilities'
    },
    {
      title: 'Manufacturing',
      icon: '🏭',
      description: 'Industrial-grade security for warehouses and factories'
    },
  ];

  const caseStudies = [
    {
      company: 'RetailPro Chain',
      industry: 'Retail',
      challenge: 'Needed to upgrade 50+ stores with modern CCTV systems',
      solution: 'Rolled out 200+ cameras across all locations in 3 months',
      result: '60% reduction in theft, 30% cost savings',
      image: '/images/1.jpg'
    },
    {
      company: 'MegaBuild Construction',
      industry: 'Construction',
      challenge: 'Temporary security for 15 active building sites',
      solution: 'Mobile surveillance units with remote monitoring',
      result: 'Zero security incidents in 12 months',
      image: '/images/2.jpg'
    },
    {
      company: 'HealthFirst Medical',
      industry: 'Healthcare',
      challenge: 'GDPR-compliant access control for sensitive areas',
      solution: 'Biometric access control with audit trails',
      result: '100% compliance, improved patient privacy',
      image: '/images/3.jpg'
    },
  ];

  const faqs = [
    {
      question: 'How do I apply for a B2B account?',
      answer: 'Simply click "Apply for B2B Account" and fill in your company details. We\'ll review your application within 24 hours and set up your account with appropriate pricing tier.'
    },
    {
      question: 'What are the payment terms?',
      answer: 'We offer Net 30 payment terms for approved accounts. New accounts start with payment on delivery, with credit facilities available after 3 months of successful trading.'
    },
    {
      question: 'Can I get custom pricing for large projects?',
      answer: 'Absolutely! For projects over £25,000, we offer custom pricing agreements. Contact your account manager or request a quote for detailed pricing.'
    },
    {
      question: 'Do you offer installation services?',
      answer: 'Yes, we have certified installation teams across the UK. Installation is free on orders over £1,000 for Professional tier and above.'
    },
    {
      question: 'What\'s the minimum order value?',
      answer: 'There is no minimum order value for B2B accounts. However, free delivery applies to orders over £500.'
    },
    {
      question: 'Can I track my team\'s orders?',
      answer: 'Yes, our B2B portal includes comprehensive analytics showing all orders, spending by department, and budget tracking in real-time.'
    },
  ];

  const process = [
    {
      step: '01',
      title: 'Apply Online',
      description: 'Complete our quick online application with your company details'
    },
    {
      step: '02',
      title: 'Verification',
      description: 'We verify your business credentials and credit references'
    },
    {
      step: '03',
      title: 'Account Setup',
      description: 'Your account is created with pricing tier and payment terms'
    },
    {
      step: '04',
      title: 'Start Ordering',
      description: 'Access the portal and start ordering at trade prices'
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-primary-800 text-white py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-900 via-primary-800 to-primary-700 opacity-90"></div>
        <div className="container-custom text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-4"
          >
            <span className="text-accent-500 uppercase text-sm tracking-widest font-semibold">
              For Trade Professionals
            </span>
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-5xl md:text-6xl font-bold mb-6"
          >
            B2B TRADE PORTAL
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-xl mb-8 text-primary-100 max-w-3xl mx-auto"
          >
            Join over 1,200 businesses saving an average of 30% on security solutions with dedicated support and flexible payment terms
          </motion.p>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Button
              text="Login to Account"
              type="primary"
              onClick={() => setShowAuthModal(true)}
            />
            <Button
              text="Apply for B2B Account"
              type="secondary"
              onClick={() => setIsApplyModalOpen(true)}
              className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-primary-800"
            />
          </motion.div>
        </div>
      </section>

      {/* Statistics */}
      <section className="py-12 bg-gray-50 border-y border-gray-200">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-accent-600 mb-2">1,200+</div>
              <div className="text-gray-600 text-sm uppercase tracking-wide">Active B2B Accounts</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-accent-600 mb-2">30%</div>
              <div className="text-gray-600 text-sm uppercase tracking-wide">Average Savings</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-accent-600 mb-2">24/7</div>
              <div className="text-gray-600 text-sm uppercase tracking-wide">Dedicated Support</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-accent-600 mb-2">98%</div>
              <div className="text-gray-600 text-sm uppercase tracking-wide">Customer Satisfaction</div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <p className="text-accent-500 uppercase text-sm tracking-widest mb-2 font-semibold">
              Why Choose Us
            </p>
            <h2 className="text-4xl font-bold text-primary-800">B2B BENEFITS</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-xl transition-all duration-300 hover:border-accent-500"
                >
                  <div className="w-14 h-14 bg-accent-100 rounded-lg flex items-center justify-center mb-4">
                    <Icon className="text-2xl text-accent-600" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2 text-gray-900">{benefit.title}</h3>
                  <p className="text-gray-600 text-sm mb-2">{benefit.description}</p>
                  <p className="text-accent-600 text-xs font-semibold uppercase tracking-wide">{benefit.detail}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing Tiers */}
      <section className="py-20 bg-gray-50">
        <div className="container-custom">
          <div className="text-center mb-16">
            <p className="text-accent-500 uppercase text-sm tracking-widest mb-2 font-semibold">
              Pricing Plans
            </p>
            <h2 className="text-4xl font-bold text-primary-800">CHOOSE YOUR TIER</h2>
            <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
              Our pricing tiers are based on annual spend, offering increasing benefits as your business grows
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {pricingTiers.map((tier, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`bg-white rounded-lg shadow-lg overflow-hidden ${
                  tier.popular ? 'ring-4 ring-accent-500 transform scale-105' : ''
                }`}
              >
                {tier.popular && (
                  <div className="bg-accent-500 text-white text-center py-2 text-sm font-semibold uppercase tracking-wide">
                    Most Popular
                  </div>
                )}
                
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{tier.name}</h3>
                  <p className="text-gray-600 mb-4">{tier.spend}</p>
                  <div className="mb-6">
                    <span className="text-4xl font-bold text-accent-600">{tier.discount}</span>
                    <span className="text-gray-600 ml-2">discount</span>
                  </div>
                  
                  <ul className="space-y-3 mb-8">
                    {tier.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start space-x-3">
                        <FiCheckCircle className="text-accent-500 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    text="Get Started"
                    type={tier.popular ? 'primary' : 'secondary'}
                    onClick={() => setIsApplyModalOpen(true)}
                    className="w-full"
                  />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries We Serve */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <p className="text-accent-500 uppercase text-sm tracking-widest mb-2 font-semibold">
              Industry Expertise
            </p>
            <h2 className="text-4xl font-bold text-primary-800">INDUSTRIES WE SERVE</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {industries.map((industry, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-all duration-300"
              >
                <div className="text-5xl mb-4">{industry.icon}</div>
                <h3 className="text-xl font-semibold mb-2 text-gray-900">{industry.title}</h3>
                <p className="text-gray-600">{industry.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-20 bg-primary-50">
        <div className="container-custom">
          <div className="text-center mb-16">
            <p className="text-accent-500 uppercase text-sm tracking-widest mb-2 font-semibold">
              Success Stories
            </p>
            <h2 className="text-4xl font-bold text-primary-800">CLIENT CASE STUDIES</h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {caseStudies.map((study, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-2xl transition-shadow duration-300"
              >
                <div className="h-48 overflow-hidden">
                  <img 
                    src={study.image} 
                    alt={study.company}
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-gray-900">{study.company}</h3>
                    <span className="text-xs px-3 py-1 bg-accent-100 text-accent-700 rounded-full font-semibold">
                      {study.industry}
                    </span>
                  </div>
                  
                  <div className="space-y-3 mb-4">
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-1">Challenge</p>
                      <p className="text-sm text-gray-700">{study.challenge}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-1">Solution</p>
                      <p className="text-sm text-gray-700">{study.solution}</p>
                    </div>
                    <div className="bg-accent-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-1">Result</p>
                      <p className="text-sm font-semibold text-accent-700">{study.result}</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <p className="text-accent-500 uppercase text-sm tracking-widest mb-2 font-semibold">
              Getting Started
            </p>
            <h2 className="text-4xl font-bold text-primary-800">HOW IT WORKS</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 max-w-5xl mx-auto">
            {process.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center relative"
              >
                <div className="w-20 h-20 bg-accent-500 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="text-xl font-semibold mb-2 text-gray-900">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
                
                {index < process.length - 1 && (
                  <div className="hidden md:block absolute top-10 left-1/2 w-full h-0.5 bg-gradient-to-r from-accent-500 to-accent-300"></div>
                )}
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button
              text="Start Your Application"
              type="primary"
              onClick={() => setIsApplyModalOpen(true)}
            />
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 bg-gray-50">
        <div className="container-custom">
          <div className="text-center mb-16">
            <p className="text-accent-500 uppercase text-sm tracking-widest mb-2 font-semibold">
              Questions & Answers
            </p>
            <h2 className="text-4xl font-bold text-primary-800">FREQUENTLY ASKED QUESTIONS</h2>
          </div>

          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-white rounded-lg shadow-md overflow-hidden"
              >
                <button
                  onClick={() => setActiveFaq(activeFaq === index ? null : index)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <span className="font-semibold text-gray-900">{faq.question}</span>
                  <span className="text-accent-500 text-2xl">
                    {activeFaq === index ? '−' : '+'}
                  </span>
                </button>
                
                {activeFaq === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="px-6 pb-4 text-gray-600"
                  >
                    {faq.answer}
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary-800 text-white">
        <div className="container-custom text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            READY TO GET STARTED?
          </h2>
          <p className="text-xl text-primary-100 mb-8 max-w-2xl mx-auto">
            Join over 1,200 businesses already saving with our B2B trade portal
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              text="Request a Quote"
              type="primary"
              onClick={() => setShowQuoteModal(true)}
            />
            <Button
              text="Apply for Account"
              type="secondary"
              onClick={() => setIsApplyModalOpen(true)}
              className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-primary-800"
            />
          </div>
        </div>
      </section>

      {/* Modals */}
      <LoginModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
      />

      <QuoteModal
        isOpen={showQuoteModal}
        onClose={() => setShowQuoteModal(false)}
      />

      {/* Application Modal */}
      <Modal
        isOpen={isApplyModalOpen}
        onClose={() => setIsApplyModalOpen(false)}
        title="Apply for B2B Account"
        size="lg"
      >
        <form className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">Company Name *</label>
              <input type="text" className="input" placeholder="Your Company Ltd" required />
            </div>
            <div>
              <label className="label">Contact Name *</label>
              <input type="text" className="input" placeholder="John Doe" required />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">Email *</label>
              <input type="email" className="input" placeholder="john@company.com" required />
            </div>
            <div>
              <label className="label">Phone *</label>
              <input type="tel" className="input" placeholder="+44 20 1234 5678" required />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">Company Registration Number *</label>
              <input type="text" className="input" placeholder="12345678" required />
            </div>
            <div>
              <label className="label">VAT Number</label>
              <input type="text" className="input" placeholder="GB123456789" />
            </div>
          </div>

          <div>
            <label className="label">Business Address *</label>
            <textarea rows={3} className="input" placeholder="Street, City, Postcode" required />
          </div>

          <div>
            <label className="label">Industry</label>
            <select className="input">
              <option value="">Select your industry</option>
              <option value="property">Property Management</option>
              <option value="retail">Retail & Hospitality</option>
              <option value="construction">Construction</option>
              <option value="education">Education</option>
              <option value="healthcare">Healthcare</option>
              <option value="manufacturing">Manufacturing</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <label className="label">Estimated Annual Spend</label>
            <select className="input">
              <option value="">Select range</option>
              <option value="0-5k">£0 - £5,000</option>
              <option value="5k-25k">£5,000 - £25,000</option>
              <option value="25k+">£25,000+</option>
            </select>
          </div>

          <Button text="Submit Application" type="primary" className="w-full" />
        </form>
      </Modal>
    </div>
  );
};

export default B2BPortalPage;
